import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type * as pdfjsLib from 'pdfjs-dist';
import { usePdfRenderer } from '../../hooks/usePdfRenderer';
import * as api from '../../api/endpoints';
import type { LayoutExport, LayoutRoom, LayoutZone, LayoutOpening } from '../../api/endpoints';

/* ═══════════════════════════════════════════════════════════════
   Full-page floor-plan layout viewer (monco.layout_export v1).

   Geometry is in PDF points of the displayed page (pdf.js
   getViewport({scale:1}) space), so the plan canvas and the SVG overlay
   share one coordinate system: a stage of page_w x page_h CSS px,
   transformed by the camera (centre cx,cy in pt; k = screen px per pt).
   Labels and the tooltip live in screen space so text stays readable.
   Port of plan-layout/layout_detection/viewer (views, labels, hover, zoom).
   ═══════════════════════════════════════════════════════════════ */

type View = 'plan' | 'high' | 'low' | 'walls';
type Kind = 'zone' | 'room' | 'door' | 'window';
type Pt = [number, number];
interface Cam { cx: number; cy: number; k: number }

const PALETTE: [number, number, number][] = [[31, 119, 180], [255, 127, 14], [44, 160, 44], [214, 39, 40],
  [148, 103, 189], [140, 86, 75], [227, 119, 194], [23, 190, 207], [188, 189, 34], [120, 200, 60],
  [0, 90, 70], [170, 0, 120], [40, 120, 90], [200, 150, 0]];
const GREY: [number, number, number] = [150, 150, 150];
const SEL = '#ff8a00';
const FILL = 0.3;
const DIM: Record<View, number> = { plan: 0, high: 0.35, low: 0.35, walls: 0.15 };
// Base plan bitmap: long side in device px. A sharper crop is rendered over
// the visible area once the camera zooms past it.
const BASE_MAX_SIDE = 5000;

const rgb = (c: number[], a = 1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const dark = (c: number[]) => `rgb(${c.map((v) => Math.round(v * 0.62)).join(',')})`;
const fmtArea = (a: number | null | undefined) => (a == null ? '' : `${(+a).toFixed(1)} m²`);
// "B.2.7 Klassenzimmer": the number, then the name in the sheet's language (German first); '' without either.
const roomLabel = (r: LayoutRoom) => [r.number, r.name_de || r.name_en].filter(Boolean).join(' ');
const ringD = (r: Pt[] | undefined | null) =>
  (r && r.length >= 3 ? 'M' + r.map((p) => `${p[0]} ${p[1]}`).join('L') + 'Z' : '');

// A stable per-room lightness shift so adjacent rooms in one zone differ.
function roomShade(c: number[], id: string): number[] {
  let h = 2166136261;
  for (const ch of String(id)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  const t = (((h >>> 0) % 1000) / 1000 - 0.5) * 0.56;
  return c.map((v) => Math.round(t >= 0 ? v + (255 - v) * t : v * (1 + t)));
}

// Label anchor: area centroid (bbox centre for degenerate rings) + half extents.
function anchor(r: Pt[]): { x: number; y: number; bw: number; bh: number } | null {
  if (!r || r.length < 3) return null;
  let a = 0, cx = 0, cy = 0, x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < r.length; i++) {
    const [px, py] = r[i], [qx, qy] = r[(i + 1) % r.length];
    const f = px * qy - qx * py;
    a += f; cx += (px + qx) * f; cy += (py + qy) * f;
    x0 = Math.min(x0, px); y0 = Math.min(y0, py); x1 = Math.max(x1, px); y1 = Math.max(y1, py);
  }
  const bw = (x1 - x0) / 2, bh = (y1 - y0) / 2;
  if (Math.abs(a) < 1e-6) return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, bw, bh };
  return { x: cx / (3 * a), y: cy / (3 * a), bw, bh };
}

// Door / window bar: the segment extruded a few points either side.
function openingD(o: LayoutOpening): string {
  const [[x0, y0], [x1, y1]] = o.segment;
  const L = Math.hypot(x1 - x0, y1 - y0) || 1, t = 4;
  const nx = -(y1 - y0) / L * t, ny = (x1 - x0) / L * t;
  return `M${x0 + nx} ${y0 + ny}L${x1 + nx} ${y1 + ny}L${x1 - nx} ${y1 - ny}L${x0 - nx} ${y0 - ny}Z`;
}

interface LabelItem { kind: Kind; id: string; text: string; x: number; y: number; bw: number; bh: number;
  prio: number; color?: string; cls: string }

export function LayoutViewer({ projectId, file, fileStem, page, title, onClose }: {
  projectId: string;
  file: string;
  fileStem: string;
  page: number;
  title: string;
  onClose: () => void;
}) {
  const { loadPdf } = usePdfRenderer();
  const viewportRef = useRef<HTMLDivElement>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement>(null);
  const detailCanvasRef = useRef<HTMLCanvasElement>(null);
  const pageRef = useRef<pdfjsLib.PDFPageProxy | null>(null);
  const detailTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const labelSizeRef = useRef<Map<string, { w: number; h: number }>>(new Map());

  const [data, setData] = useState<LayoutExport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [planReady, setPlanReady] = useState(false);
  const [pageSize, setPageSize] = useState<Pt | null>(null);
  const [view, setView] = useState<View>('high');
  const [labels, setLabels] = useState(true);
  const [size, setSize] = useState({ W: 0, H: 0 });
  const [cam, setCam] = useState<Cam | null>(null);
  const [hover, setHover] = useState<{ kind: Kind; id: string; x: number; y: number } | null>(null);
  const [sel, setSel] = useState<{ kind: Kind; id: string } | null>(null);
  const [detail, setDetail] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [labelTick, forceLabels] = useState(0);

  // ── Data ──
  useEffect(() => {
    let cancelled = false;
    api.pmGetLayoutPage(projectId, fileStem, page).then((d) => {
      if (cancelled) return;
      if (!d) setError('Could not load the layout for this page.');
      else if (d.status !== 'ok') setError(`Layout status: ${d.status}${d.reason ? ` (${d.reason})` : ''}`);
      else setData(d);
    });
    return () => { cancelled = true; };
  }, [projectId, fileStem, page]);

  // ── Plan background: one base bitmap of the whole page ──
  useEffect(() => {
    let cancelled = false;
    let task: pdfjsLib.RenderTask | null = null;
    (async () => {
      const doc = await loadPdf(api.pdfUrl(file));
      if (!doc || cancelled) { if (!doc && !cancelled) setError((e) => e ?? 'Could not load the plan PDF.'); return; }
      const pg = await doc.getPage(page);
      if (cancelled) return;
      pageRef.current = pg;
      const vp1 = pg.getViewport({ scale: 1 });
      setPageSize((s) => s ?? [vp1.width, vp1.height]);
      const canvas = baseCanvasRef.current;
      if (!canvas) return;
      const s = Math.min(2.5, BASE_MAX_SIDE / Math.max(vp1.width, vp1.height));
      const vp = pg.getViewport({ scale: s });
      canvas.width = Math.round(vp.width);
      canvas.height = Math.round(vp.height);
      task = pg.render({ canvasContext: canvas.getContext('2d')!, viewport: vp });
      try { await task.promise; if (!cancelled) setPlanReady(true); } catch { /* cancelled */ }
    })();
    return () => { cancelled = true; try { task?.cancel(); } catch { /* ignore */ } };
  }, [file, page, loadPdf]);

  // Page size: the export's own value wins (it defines the coordinate space).
  const pw = data?.units?.page_size_pt?.[0] ?? pageSize?.[0] ?? 0;
  const ph = data?.units?.page_size_pt?.[1] ?? pageSize?.[1] ?? 0;

  // ── Viewport size ──
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ W: r.width, H: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit target: the plan region when the export has one, else the page.
  const fitBox = useMemo(() => {
    const r = data?.region;
    if (r && r.length === 4 && r[2] > r[0] && r[3] > r[1]) return { x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1] };
    return pw && ph ? { x: 0, y: 0, w: pw, h: ph } : null;
  }, [data, pw, ph]);
  const fitK = fitBox && size.W && size.H
    ? Math.min((size.W - 56) / fitBox.w, (size.H - 56) / fitBox.h) : 1;

  const fit = useCallback(() => {
    if (!fitBox || !size.W) return;
    setCam({ cx: fitBox.x + fitBox.w / 2, cy: fitBox.y + fitBox.h / 2, k: fitK });
  }, [fitBox, fitK, size.W]);

  useEffect(() => { if (!cam && fitBox && size.W) fit(); }, [cam, fitBox, size.W, fit]);

  // ── Zoom (wheel, non-passive so the page does not scroll) ──
  const camRef = useRef<Cam | null>(null);
  camRef.current = cam;
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const c = camRef.current;
      if (!c) return;
      const r = el.getBoundingClientRect();
      const sx = e.clientX - r.left, sy = e.clientY - r.top;
      const f = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
      const nk = Math.min(12, Math.max(fitK * 0.4, c.k * f));
      const px = (sx - r.width / 2) / c.k + c.cx, py = (sy - r.height / 2) / c.k + c.cy;
      setCam({ cx: px - (sx - r.width / 2) / nk, cy: py - (sy - r.height / 2) / nk, k: nk });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [fitK]);

  // ── Pan (drag) + click-to-select ──
  const drag = useRef<{ x: number; y: number; cam: Cam; moved: boolean } | null>(null);
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || !cam) return;
    drag.current = { x: e.clientX, y: e.clientY, cam, moved: false };
  };
  const hitOf = (t: EventTarget | null): { kind: Kind; id: string } | null => {
    const n = (t as Element | null)?.closest?.('[data-kind]') as HTMLElement | null;
    return n ? { kind: n.dataset.kind as Kind, id: n.dataset.id || '' } : null;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const d = drag.current;
    if (d) {
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (!d.moved && Math.hypot(dx, dy) > 3) d.moved = true;
      if (d.moved) { setCam({ ...d.cam, cx: d.cam.cx - dx / d.cam.k, cy: d.cam.cy - dy / d.cam.k }); setHover(null); return; }
    }
    const r = viewportRef.current!.getBoundingClientRect();
    const h = hitOf(e.target);
    setHover(h ? { ...h, x: e.clientX - r.left, y: e.clientY - r.top } : null);
  };
  const onMouseUp = (e: React.MouseEvent) => {
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved) {
      const h = hitOf(e.target);
      setSel(h && !(sel && sel.kind === h.kind && sel.id === h.id) ? h : null);
    }
  };

  // Esc closes; F fits.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      else if ((e.key === 'f' || e.key === 'F') && !(e.target instanceof HTMLInputElement)) fit();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, fit]);

  // ── Sharp crop of the visible area once the base bitmap is upscaled ──
  useEffect(() => {
    if (!cam || !planReady || !pw || !size.W) return;
    const dpr = window.devicePixelRatio || 1;
    const baseS = Math.min(2.5, BASE_MAX_SIDE / Math.max(pw, ph));
    if (cam.k * dpr <= baseS * 1.25) { setDetail(null); return; }
    const t = window.setTimeout(async () => {
      const pg = pageRef.current, canvas = detailCanvasRef.current;
      if (!pg || !canvas) return;
      const x = cam.cx - size.W / 2 / cam.k, y = cam.cy - size.H / 2 / cam.k;
      const s = cam.k * dpr;
      const off = document.createElement('canvas');
      off.width = Math.round(size.W * dpr); off.height = Math.round(size.H * dpr);
      try { detailTaskRef.current?.cancel(); } catch { /* ignore */ }
      const vp = pg.getViewport({ scale: s, offsetX: -x * s, offsetY: -y * s });
      const task = pg.render({ canvasContext: off.getContext('2d')!, viewport: vp });
      detailTaskRef.current = task;
      try {
        await task.promise;
        canvas.width = off.width; canvas.height = off.height;
        canvas.getContext('2d')!.drawImage(off, 0, 0);
        setDetail({ x, y, w: size.W / cam.k, h: size.H / cam.k });
      } catch { /* superseded */ }
    }, 250);
    return () => window.clearTimeout(t);
  }, [cam, planReady, pw, ph, size.W, size.H]);
  useEffect(() => () => { try { detailTaskRef.current?.cancel(); } catch { /* ignore */ } }, []);

  // ── Scene ──
  const scene = useMemo(() => {
    const zones = data?.zones || [], rooms = data?.rooms || [];
    const zoneColor: Record<string, number[]> = {};
    const zoneById: Record<string, LayoutZone> = {};
    const roomById: Record<string, LayoutRoom> = {};
    zones.forEach((z, i) => { zoneColor[z.id] = PALETTE[i % PALETTE.length]; zoneById[z.id] = z; });
    rooms.forEach((r) => { roomById[r.id] = r; });
    const openById: Record<string, LayoutOpening> = {};
    (data?.doors || []).forEach((o) => { openById[o.id] = o; });
    (data?.windows || []).forEach((o) => { openById[o.id] = o; });
    const wallsD = (data?.walls || []).map((w) => ringD(w.outline)).join('');
    return { zones, rooms, zoneColor, zoneById, roomById, openById, wallsD };
  }, [data]);

  const labelItems = useMemo<LabelItem[]>(() => {
    const out: LabelItem[] = [];
    if (view === 'high') {
      for (const z of scene.zones) {
        const a = anchor(z.polygon); if (!a) continue;
        const name = z.name || z.id;
        out.push({ kind: 'zone', id: z.id, text: name.length > 56 ? name.slice(0, 54) + '…' : name, ...a,
          prio: a.bw * a.bh, color: dark(scene.zoneColor[z.id] || GREY), cls: 'zone' });
      }
    } else if (view === 'low') {
      for (const r of scene.rooms) {
        const a = anchor(r.polygon); if (!a) continue;
        const name = roomLabel(r);
        const named = name !== '';
        const area = r.area_m2 != null ? fmtArea(r.area_m2) : '';
        const printed = r.printed_area_m2 != null ? ` (${fmtArea(r.printed_area_m2)})` : '';
        const text = named ? `${name}${area ? `\n${area}${printed}` : ''}` : (area ? area + printed : r.id);
        out.push({ kind: 'room', id: r.id, text, ...a, prio: (named ? 1e12 : 0) + (r.area_m2 || 0), cls: named ? 'name' : 'area' });
      }
    }
    return out.sort((a, b) => b.prio - a.prio);
  }, [view, scene]);

  // Measure label boxes once they exist in the DOM (a second pass places them).
  const labelsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = labelsRef.current;
    if (!box) return;
    let changed = false;
    box.querySelectorAll<HTMLElement>('[data-lkey]').forEach((n) => {
      const key = n.dataset.lkey!;
      if (!labelSizeRef.current.has(key)) {
        labelSizeRef.current.set(key, { w: n.offsetWidth, h: n.offsetHeight });
        changed = true;
      }
    });
    if (changed) forceLabels((x) => x + 1);
  });

  // Greedy placement: biggest first, skip a label that would overlap one
  // already placed or does not fit inside its own room on screen.
  const placed = useMemo(() => {
    if (!cam || !labels || !size.W) return [];
    const res: { it: LabelItem; key: string; sx: number; sy: number; measured: boolean; sel: boolean }[] = [];
    const boxes: number[][] = [];
    const ordered = sel ? [...labelItems.filter((i) => i.id === sel.id && i.kind === sel.kind),
      ...labelItems.filter((i) => !(i.id === sel.id && i.kind === sel.kind))] : labelItems;
    for (const it of ordered) {
      const key = `${it.kind}:${it.id}:${it.text}`;
      const m = labelSizeRef.current.get(key);
      const isSel = !!sel && sel.id === it.id && sel.kind === it.kind;
      if (!m) { res.push({ it, key, sx: -9999, sy: -9999, measured: false, sel: isSel }); continue; }
      let sx = (it.x - cam.cx) * cam.k + size.W / 2 - m.w / 2;
      let sy = (it.y - cam.cy) * cam.k + size.H / 2 - m.h / 2;
      if (sx > size.W || sy > size.H || sx + m.w < 0 || sy + m.h < 0) {
        if (it.kind !== 'zone') continue;
        // zone labels stay on screen while any part of the zone is visible
        const zx0 = (it.x - it.bw - cam.cx) * cam.k + size.W / 2, zx1 = (it.x + it.bw - cam.cx) * cam.k + size.W / 2;
        const zy0 = (it.y - it.bh - cam.cy) * cam.k + size.H / 2, zy1 = (it.y + it.bh - cam.cy) * cam.k + size.H / 2;
        if (!(zx1 > 0 && zx0 < size.W && zy1 > 0 && zy0 < size.H)) continue;
        sx = Math.max(6, Math.min(size.W - m.w - 6, sx)); sy = Math.max(6, Math.min(size.H - m.h - 6, sy));
      }
      if (!isSel && it.kind === 'room' && (it.bw * cam.k < m.w * 0.55 || it.bh * cam.k < m.h * 0.8)) continue;
      if (!isSel && it.kind === 'zone' && Math.max(it.bw, it.bh) * cam.k < 40) continue;
      const b = [sx - 3, sy - 3, sx + m.w + 3, sy + m.h + 3];
      if (!isSel && boxes.some((o) => b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1])) continue;
      boxes.push(b);
      res.push({ it, key, sx, sy, measured: true, sel: isSel });
    }
    return res;
    // labelSizeRef is read; labelTick bumps when new labels were measured
  }, [cam, labels, size.W, size.H, labelItems, sel, labelTick]);

  // ── Tooltip ──
  const tip = useMemo(() => {
    if (!hover) return null;
    const { zoneById, roomById, openById } = scene;
    const roomName = (rid: string | null) => {
      const r = rid ? roomById[rid] : null;
      return r ? (roomLabel(r) || r.id) : 'outside';
    };
    if (hover.kind === 'zone') {
      const z = zoneById[hover.id]; if (!z) return null;
      const rooms = (z.room_ids || []).map((rid) => roomById[rid]).filter(Boolean);
      const area = rooms.reduce((s, r) => s + (r.area_m2 || 0), 0);
      return { head: z.name || z.id, lines: [
        [z.kind, `${rooms.length} room${rooms.length === 1 ? '' : 's'}`, area ? fmtArea(area) : ''].filter(Boolean).join(' · '),
        rooms.map((r) => roomLabel(r) || r.id).join(', '),
        z.summary || '',
      ].filter(Boolean) };
    }
    if (hover.kind === 'room') {
      const r = roomById[hover.id]; if (!r) return null;
      const z = r.zone_id ? zoneById[r.zone_id] : null;
      const lines = [`area ${fmtArea(r.area_m2) || '—'}`];
      if (r.printed_area_m2 != null) {
        const d = r.area_m2 != null ? r.area_m2 - r.printed_area_m2 : null;
        lines.push(`printed ${fmtArea(r.printed_area_m2)}${d != null ? ` (${d >= 0 ? '+' : ''}${d.toFixed(1)} m²)` : ''}`);
      }
      lines.push(`zone: ${z ? (z.name || z.id) : 'none'}`);
      if (r.outdoor) lines.push('outdoor');
      return { head: roomLabel(r) || `Room ${r.id}`, lines };
    }
    const o = openById[hover.id]; if (!o) return null;
    return { head: `${hover.kind === 'door' ? 'Door' : 'Window'} ${o.id}`, lines: [
      o.width_m != null ? `width ${o.width_m.toFixed(2)} m` : '',
      (o.rooms || []).map(roomName).join(' ↔ '),
    ].filter(Boolean) };
  }, [hover, scene]);

  // ── Render ──
  const k = cam?.k || 1;
  const stageStyle: React.CSSProperties = cam ? {
    width: pw, height: ph,
    transform: `translate(${size.W / 2 - cam.cx * k}px, ${size.H / 2 - cam.cy * k}px) scale(${k})`,
    ['--sw' as string]: `${1 / k}px`,
  } : { width: pw, height: ph, visibility: 'hidden' };
  const sw = (n: number) => `calc(var(--sw) * ${n})`;
  const isSel = (kind: Kind, id: string) => !!sel && sel.kind === kind && sel.id === id;
  const counts = data ? `${data.zones?.length ?? 0} zones · ${data.rooms?.length ?? 0} rooms · ${data.walls?.length ?? 0} walls · ${data.doors?.length ?? 0} doors · ${data.windows?.length ?? 0} windows` : '';
  const noScale = !!data?.warnings?.includes('no_scale');
  const scaleTxt = noScale ? ' · no scale, sizes not to scale' : data?.units?.scale ? ` · 1:${data.units.scale}` : '';

  const zonePaths = view === 'high' && scene.zones.map((z) => {
    const c = scene.zoneColor[z.id];
    const d = [z.polygon, ...(z.parts || [])].map(ringD).join('') + (z.holes || []).map(ringD).join('');
    const dash = z.kind === 'exterior' ? '10 6' : z.kind === 'circulation' ? '4 4' : undefined;
    return (
      <path key={z.id} data-kind="zone" data-id={z.id} d={d} fillRule="evenodd"
        fill={rgb(c)} fillOpacity={FILL} stroke={isSel('zone', z.id) ? SEL : rgb(c)}
        style={{ strokeWidth: sw(isSel('zone', z.id) ? 4 : 2.5) }} strokeDasharray={dash ? dash.split(' ').map((v) => +v / k).join(' ') : undefined}
        strokeLinejoin="round" />
    );
  });
  const roomPaths = view === 'low' && scene.rooms.map((r) => {
    const c = roomShade(scene.zoneColor[r.zone_id || ''] || GREY, r.id);
    return (
      <path key={r.id} data-kind="room" data-id={r.id} d={ringD(r.polygon)}
        fill={rgb(c)} fillOpacity={r.outdoor ? FILL * 0.4 : FILL}
        stroke={isSel('room', r.id) ? SEL : dark(c)} strokeOpacity={0.95}
        style={{ strokeWidth: sw(isSel('room', r.id) ? 3.5 : 1.4) }}
        strokeDasharray={r.outdoor ? `${5 / k} ${4 / k}` : undefined} />
    );
  });
  const openPaths = (list: LayoutOpening[] | undefined, kind: 'door' | 'window') => (list || []).map((o) => {
    const c = kind === 'door' ? '#d000d0' : '#0066ff';
    return <path key={`${kind}${o.id}`} data-kind={kind} data-id={o.id} d={openingD(o)} fill={c}
      fillOpacity={0.8} stroke={isSel(kind, o.id) ? SEL : c} style={{ strokeWidth: sw(isSel(kind, o.id) ? 3 : 1) }} />;
  });

  return createPortal(
    <div className="layout-viewer-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="layout-viewer">
        <div className="layout-viewer-bar">
          <div className="layout-viewer-title">
            <strong>{title}</strong>
            <span className="dim">{file} · p.{page}{scaleTxt}{counts ? ` · ${counts}` : ''}</span>
          </div>
          <div className="layout-viewer-controls">
            <div className="layout-seg" role="group" aria-label="View">
              {(['plan', 'high', 'low', 'walls'] as View[]).map((v) => (
                <button key={v} className={view === v ? 'on' : ''} onClick={() => setView(v)}
                  title={{ plan: 'The plan only', high: 'High level: zones', low: 'Low level: rooms', walls: 'Walls, doors and windows' }[v]}>
                  {{ plan: 'Plan', high: 'High', low: 'Low', walls: 'Walls' }[v]}
                </button>
              ))}
            </div>
            <label className="layout-check">
              <input type="checkbox" checked={labels} onChange={(e) => setLabels(e.target.checked)} /> Labels
            </label>
            <button className="btn-review-action" onClick={fit} title="Fit the plan (F)">Fit</button>
            <span className="dim layout-zoom">{cam ? `${Math.round((cam.k / fitK) * 100)}%` : ''}</span>
            <button className="ann-detail-close" onClick={onClose} title="Close (Esc)">&times;</button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="layout-viewport"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => { drag.current = null; setHover(null); }}
        >
          <div className="layout-stage" style={stageStyle}>
            <canvas ref={baseCanvasRef} className="layout-plan" style={{ width: pw, height: ph }} />
            <canvas ref={detailCanvasRef} className="layout-plan"
              style={detail ? { left: detail.x, top: detail.y, width: detail.w, height: detail.h } : { display: 'none' }} />
            {pw > 0 && (
              <svg className="layout-svg" width={pw} height={ph} viewBox={`0 0 ${pw} ${ph}`}>
                <rect x={0} y={0} width={pw} height={ph} fill="#fff" opacity={DIM[view]} pointerEvents="none" />
                {zonePaths}
                {roomPaths}
                {(view === 'walls' || view === 'low') && (
                  <path d={scene.wallsD} fill="#e00000" fillOpacity={view === 'walls' ? 0.55 : 0.35}
                    stroke="#e00000" style={{ strokeWidth: sw(view === 'walls' ? 1.5 : 1) }} pointerEvents="none" />
                )}
                {view !== 'plan' && openPaths(data?.doors, 'door')}
                {view !== 'plan' && openPaths(data?.windows, 'window')}
              </svg>
            )}
          </div>
          <div className="layout-labels" ref={labelsRef}>
            {view !== 'plan' && view !== 'walls' && placed.map(({ it, key, sx, sy, measured, sel: s }) => (
              <div key={key} data-lkey={key} className={`layout-lbl ${it.cls}${s ? ' sel' : ''}`}
                style={{ transform: `translate(${Math.round(sx)}px, ${Math.round(sy)}px)`,
                  visibility: measured ? 'visible' : 'hidden', color: it.color }}>
                {it.text}
              </div>
            ))}
          </div>
          {tip && hover && (
            <div className="layout-tip" style={{
              left: Math.min(size.W - 300, hover.x + 14), top: hover.y + 16 + 120 > size.H ? hover.y - 130 : hover.y + 16 }}>
              <div className="h">{tip.head}</div>
              {tip.lines.map((l, i) => <div key={i} className="s">{l}</div>)}
            </div>
          )}
          {(error || !data || !planReady) && (
            <div className="layout-status">
              {error ? error : !data ? <><span className="ann-btn-spinner" />Loading layout…</> : <><span className="ann-btn-spinner" />Rendering plan…</>}
            </div>
          )}
        </div>
        <div className="layout-viewer-foot dim">
          Wheel to zoom · drag to pan · click to select · F fits · Esc closes
          {view === 'walls' && <> · <span style={{ color: '#ff5050' }}>walls</span> · <span style={{ color: '#e040e0' }}>doors</span> · <span style={{ color: '#4f8cff' }}>windows</span></>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
